import LogOperations from '@modules/husleie-framework/src/static-classes/LogOperations.class';
import UserStringOperations from '@modules/husleie-framework/src/static-classes/UserStringOperations.class';
import { Contract } from '@modules/husleie-framework/src/types/Contract.entity';
import { SigningStatusEnum, Signer, SignMethodEnum } from '@modules/husleie-framework/src/types/Signing.entity';
import ContractConversionOperations from '@modules/store/contract/static-classes/ContractConversionOperations';
import type {
  ContractAPIInterface,
  ContractCreate,
  ContractCreateResponse,
  ContractDownload,
  ContractDownloadResponse,
  ContractGet,
  ContractGetResponse,
  ContractPreview,
  ContractPreviewResponse,
  ContractSigningMethodGet,
  ContractSigningMethodGetResponse,
  ContractReadyForSigning,
  ContractReadyForSigningResponse,
  ContractUpdate,
  ContractUpdateResponse,
  ContractFeedback,
  ContractPendingSignaturesGet,
  ContractPendingSignaturesGetResponse,
} from '@modules/store/contract/types/ContractAPI.interface';
import { RentalObjectAPIProtocolVersionEnum } from '@modules/store/rentalObject/types/RentalObjectAPI.interface';
import { ContractApi, ContractSigningApi } from '@modules/smartmieteapi';

export default class API implements ContractAPIInterface {
  getContract(get: ContractGet): Promise<ContractGetResponse> {
    return new Promise((resolve, reject) => {
      const api = new ContractApi();
      api
        .getContractSession(get.uuid)
        .then((data: any) => {
          const contract: Contract = ContractConversionOperations.apiToInternal(
            {
              ...data.contract,
              uuid: data.id,
              status: data.status,
              created: data.created,
              modified: data.modified,
            },
            get.userTranslationOptions,
            { rentalObjectAPIProtocolVersion: RentalObjectAPIProtocolVersionEnum.v2_0_0 }
          );
          contract.landlord.id = data.meta.landlord_id;
          contract.rentalObject.id = data.meta.rental_object_id;

          resolve({ contract });
        })
        .catch((error: any) => {
          LogOperations.error('API|getContractSession', error.message);
          reject(error);
        });
    });
  }

  createContract(create: ContractCreate): Promise<ContractCreateResponse> {
    return new Promise((resolve, reject) => {
      const api = new ContractApi();
      const body: any = { rental_object_id: create.rentalObjectId, tenancy_id: create.tenancyId };
      api
        .createContractSession({ body })
        .then((data: any) => {
          const contract: Contract = ContractConversionOperations.apiToInternal(
            {
              ...data.contract,
              uuid: data.id,
              status: data.status,
              created: data.created,
              modified: data.modified,
            },
            create.userTranslationOptions,
            { rentalObjectAPIProtocolVersion: RentalObjectAPIProtocolVersionEnum.v2_0_0 }
          );
          contract.landlord.id = data.meta.landlord_id;
          contract.rentalObject.id = data.meta.rental_object_id;

          resolve({ contract });
        })
        .catch((error: any) => {
          LogOperations.error('API|createContractSession', error.message);
          reject(error);
        });
    });
  }

  updateContract(update: ContractUpdate): Promise<ContractUpdateResponse> {
    return new Promise((resolve, reject) => {
      const api = new ContractApi();
      const body: any = ContractConversionOperations.internalToApi(update.contract, {
        rentalObjectAPIProtocolVersion: RentalObjectAPIProtocolVersionEnum.v2_0_0,
      });
      api
        .updateContractSession(update.contract.uuid, { body })
        .then((data: any) => {
          resolve({
            contract: {
              ...update.contract,
              status: data.status,
              created: data.created,
              modified: data.modified,
            },
          });
        })
        .catch((error: any) => {
          LogOperations.error('API|updateContractSession', error.message);
          reject(error);
        });
    });
  }

  updateContractTenantDetails(update: ContractUpdate): Promise<ContractUpdateResponse> {
    return new Promise((resolve, reject) => {
      const api = new ContractApi();
      let body: any = ContractConversionOperations.internalToApi(update.contract, {
        rentalObjectAPIProtocolVersion: RentalObjectAPIProtocolVersionEnum.v2_0_0,
      });

      body = {
        tenant: body.tenant,
        coTenants: body.coTenants,
      };

      api
        .updateContractTenantDetails(update.contract.uuid, { body })
        .then((data: any) => {
          resolve({
            contract: {
              ...update.contract,
              status: data.status,
              created: data.created,
              modified: data.modified,
            },
          });
        })
        .catch((error: any) => {
          LogOperations.error('API|updateContractTenantDetails', error.message);
          reject(error);
        });
    });
  }

  fetchContractPreview(preview: ContractPreview): Promise<ContractPreviewResponse> {
    return new Promise((resolve, reject) => {
      const api = new ContractApi();
      const query: any = { format: preview.format };

      api
        .contract_preview(preview.uuid, { query }, preview.responseType)
        .then((data: any) => {
          switch (preview.format) {
            case 'html':
              resolve({ html: data });
              break;
            case 'pdf':
              resolve({ pdf: data });
              break;
          }
        })
        .catch((error: any) => {
          LogOperations.error('API|contract_preview', error.message);
          reject(error);
        });
    });
  }

  getContractDownloadUrl(download: ContractDownload): Promise<ContractDownloadResponse> {
    return new Promise((resolve, reject) => {
      const api = new ContractApi();
      const query: any = download.format;
      api
        .contract_download(download.uuid, query)
        .then((url: any) => {
          resolve({ url });
        })
        .catch((error: any) => {
          LogOperations.error('API|contract_download', error.message);
          reject(error);
        });
    });
  }

  sendContractForTenantSigning(sign: ContractReadyForSigning): Promise<ContractReadyForSigningResponse> {
    return new Promise((resolve, reject) => {
      const api = new ContractApi();
      const signingMethod = sign.method === SignMethodEnum.DIGITAL ? 'canvas' : sign.method;
      const body: any = {
        callbacks: {
          landlord: {},
          tenant: {},
        },
        signingMethod,
      };

      api
        .sendContractForSigning(sign.uuid, { body })
        .then(() => {
          resolve({ status: SigningStatusEnum.SIGNING_TENANT });
        })
        .catch((error: any) => {
          LogOperations.error('API|sendContractForSigning', error.message);
          reject(error);
        });
    });
  }

  async getContractSigningMethod(get: ContractSigningMethodGet): Promise<ContractSigningMethodGetResponse> {
    return new Promise((resolve, reject) => {
      const api = new ContractSigningApi();
      api
        .getContractSigningMethod(get.uuid)
        .then((data: any) => {
          const signingMethod: string = data.signingMethod === 'canvas' ? SignMethodEnum.DIGITAL : data.signingMethod;
          resolve({ signingMethod, signingStatus: data.signingStatus });
        })
        .catch((error: any) => {
          LogOperations.error('API|getContractSigningMethod', error.message, error.response?.data);
          reject(error);
        });
    });
  }

  sendContractTenantFeedback(feedback: ContractFeedback): Promise<void> {
    return new Promise((resolve, reject) => {
      const api = new ContractApi();
      const body: any = {
        message: feedback.contract.tenantFeedback,
      };
      api
        .sendContractFeedback(feedback.contract.uuid, { body })
        .then(() => {
          resolve();
        })
        .catch((error: any) => {
          LogOperations.error('API|sendContractFeedback', error.message);
          reject(error);
        });
    });
  }

  getContractPendingSignatures(get: ContractPendingSignaturesGet): Promise<ContractPendingSignaturesGetResponse> {
    return new Promise((resolve, reject) => {
      const api = new ContractSigningApi();
      api
        .getContractPendingSignatures(get.uuid)
        .then((data: any) => {
          const signers: Signer[] = data.signers.map((entry: any) => {
            const fullName: string = UserStringOperations.getFullName(
              UserStringOperations.getAppellationTranslation(get.userTranslationOptions, entry.title.common),
              UserStringOperations.getTitleTranslation(get.userTranslationOptions, entry.title.professional),
              entry.givenName,
              entry.familyName
            );

            return {
              uuid: entry.id.toString(),
              fullName,
              signature: entry.signature,
              type: entry.type,
              status: entry.status,
              uploadToken: data.uploadToken,
            };
          });
          resolve({ status: data.status, signers });
        })
        .catch((error: any) => {
          LogOperations.error('API|getContractPendingSignatures', error.message);
          reject(error);
        });
    });
  }

  async signContract(contractId: string, signatureId: string, fileToken: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const api = new ContractSigningApi();
      api
        .signContract(contractId, signatureId, { body: { signatureFileToken: fileToken } })
        .then(() => {
          resolve(true);
        })
        .catch((error: any) => {
          LogOperations.error('API|signContract', error.message);
          reject(error);
        });
    });
  }

  confirmOfflineContractSigning(contractId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const api = new ContractSigningApi();
      api
        .confirmOfflineContractSigning(contractId)
        .then(() => {
          resolve();
        })
        .catch((error: any) => {
          LogOperations.error('API|confirmOfflineContractSigning', error.message);
          reject(error);
        });
    });
  }
}
